import React, { useEffect, useRef, useState } from "react";
import { Box, Grid2, TextField, Typography, useTheme } from "@mui/material";
import FAQAccordion from "./FAQAccordion";


const faqDataGrouped = [
    {
        category: "Schedules",
        questions: [
            {
                q: "How do I access the total number of critical activities for my project?",
                a: "Navigate to the project's Schedule module and view the 'Total Number of Critical Activities' KPI."
            },
            {
                q: "What does 'Delay In Days' indicate in the schedule?",
                a: "'Delay In Days' reflects the total number of days a project is delayed beyond the planned timeline."
            },
            {
                q: "How is 'Critical Activity Variance' calculated?",
                a: "It measures the variance between planned and actual timelines specific to critical activities."
            },
            {
                q: "What does 'Schedule Variance (SV)' represent?",
                a: "Schedule Variance captures the difference between planned and actual progress values in terms of days."
            },
            {
                q: "Where can I find the Forecast Completion Date?",
                a: "Within the Schedule module, check the project timeline section for the 'Forecast Completion Date', estimating when the project is anticipated to finish."
            },
            {
                q: "How is the 'Physical Progress Plan V/s Actual' KPI useful?",
                a: "This KPI provides a comparison between the planned progress and actual progress achieved as of the current date."
            },
            {
                q: "How do I interpret the S-Curve in project schedules?",
                a: "The S-Curve graphically represents the cumulative progress over time, showing planned, actual, and revised progress lines for easy comparison."
            }
        ]
    },
    {
        category: "Engineering",
        questions: [
            {
                q: "What indicates engineering delays in project schedule?",
                a: "The KPI 'Delay in Days for Engineering' highlights specific delays within engineering activities."
            },
            {
                q: "How are engineering document submissions monitored?",
                a: "Use 'Avg Time to Approve a Document' and 'Number of Documents Under Review' KPIs to monitor document workflows."
            },
            {
                q: "How do I track engineering progress?",
                a: "The Engineering Schedule module includes the 'Progress S-Curve' to visualize planned, actual, and revised engineering progress."
            }
        ]
    },
    {
        category: "Construction",
        questions: [
            {
                q: "Can I view construction critical activities?",
                a: "Yes, under the Construction module, check 'Total Construction Critical Activities' to view and manage ongoing tasks."
            },
            {
                q: "What does 'Construction Schedule Variance' tell us?",
                a: "It indicates the difference between the planned and actual timeline achievements in construction activities."
            }
        ]
    },
    {
        category: "Productivity & Manpower",
        questions: [
            {
                q: "How can I assess manpower productivity?",
                a: "The 'Manpower Plan Vs Actual Vs Variance' KPI helps measure manpower effectiveness in relation to planned performance."
            },
            {
                q: "What does 'Manpower Histogram' show?",
                a: "This visual representation displays manpower allocation and usage over time for quick evaluation of demands."
            },
            {
                q: "How do I manage upcoming manpower requirements?",
                a: "Use the 'Upcoming Manpower Requirement' KPI to forecast and prepare for future manpower needs."
            }
        ]
    },
    {
        category: "Risk Management",
        questions: [
            {
                q: "How do I assess active project risks?",
                a: "'Total Active Risk' and 'Active Risk by Severity Level' KPIs showcase current risks by severity, aiding prioritization."
            },
            {
                q: "What is a 'Heat Matrix'?",
                a: "It visually maps risk severity against likelihood, providing a comprehensive overview of project risk exposure."
            }
        ]
    },
    {
        category: "Quality Assurance",
        questions: [
            {
                q: "Can I monitor NCR status?",
                a: "Yes, use 'Safety NCR Open vs Close' and 'Distribution of NCR with Open Status' KPIs for real-time non-conformance reporting."
            },
            {
                q: "How is 'Avg Duration of Closure' calculated for quality issues?",
                a: "This KPI measures how long each quality-related NCR takes to resolve from open to close status."
            }
        ]
    },
    {
        category: "Financial Progress & Cashflow",
        questions: [
            {
                q: "How do I track budget variances?",
                a: "'Budgeted Cost Vs EAC Vs Actual' and 'Cost Performance Index' KPIs allow monitoring of financial discrepancies across project phases."
            },
            {
                q: "What is 'Earned Value Management (EVM)' used for?",
                a: "EVM measures project performance by comparing planned work and budget (Planned Value) to actual accomplishments (Earned Value)."
            }
        ]
    },
    {
        category: "Procurement",
        questions: [
            {
                q: "How can S-Curve analysis benefit procurement management?",
                a: "The S-Curve in procurement illustrates trends in material receipt and expenditure, enabling proactive management of procurement timelines."
            },
            {
                q: "How is the 'Material Variation Items' KPIs useful?",
                a: "It tracks discrepancies in material quantities compared to initial procurement plans, helping manage purchasing accuracy."
            }
        ]
    },
    {
        category: "Vendor Performance",
        questions: [
            {
                q: "What insights are available for vendor evaluation?",
                a: "'Top Performing Vendors' and 'Non-Performing Vendors' KPIs enable assessment of vendor effectiveness relative to project requirements."
            },
            {
                q: "How do I manage specialised contractors?",
                a: "'Status for Specialised Contractor' KPIs evaluate the performance and reliability of contractors with specific expertise."
            }
        ]
    },
    {
        category: "Claims and Change Orders",
        questions: [
            {
                q: "How do I track change orders?",
                a: "Use 'Total Change Orders' and 'Cost of Change Orders' KPIs for assessing financial impacts and managing revisions to project scope."
            },
            {
                q: "What does 'Impact of Claims' indicate?",
                a: "This KPI quantifies the effect of claims in terms of project duration and cost adjustments, facilitating negotiations and approvals."
            }
        ]
    },
    {
        category: "Montecarlo Simulation",
        questions: [
            {
                q: "How does the tool forecast project completion scenarios (Best Worst & Most Likely)?",
                a: "Based on historical progress data and activity rates the tool utilizes the montecarlo simulation to predict probable completion dates (Best Worst & Most Likely), comparing them with planned completion dates to flag potential shifts."
            }
        ]
    }
];


const DataSets = () => {
    const theme = useTheme();
    const [search, setSearch] = useState("");

    // Filter the grouped data based on search
    const filteredData = faqDataGrouped
        .map((category) => ({
            ...category,
            questions: category.questions.filter((qa) =>
                qa.q.toLowerCase().includes(search.toLowerCase())
            ),
        }))
        .filter((category) => category.questions.length > 0);

    return (
        <>
            <Grid2 container spacing={2} sx={{ mt: '20px' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5" sx={{ fontSize: '24px', fontWeight: '500', color: '#252525', mb: 2, lineHeight: "32px", letterSpacing: '-0.4px' }}>
                        FAQ on data sets
                    </Typography>
                </Box>
            </Grid2>
            <Grid2 container spacing={2} sx={{ mt: '20px' }}>
                <Grid2 size={12}>
                    <Box mb={4}>
                        <TextField
                            fullWidth
                            placeholder="Search"
                            variant="outlined"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{
                                background: "#fff",
                            }}
                        />
                    </Box>
                    <FAQAccordion faqDataGrouped={filteredData} />

                </Grid2>
            </Grid2>
        </>
    );
};

export default DataSets;
